import { useMemo, useState } from "react";

import formatComma from "react-lib/utils/formatComma";

import { AlertV4, ButtonV4, FieldV4, ModalV4 } from "../common/ui";

import type { DepositData, DepositState } from "../common/sequence/BillingWalletV4";
import BillingPayLineV4 from "./BillingPayLineV4";
import {
  emptyPayLine,
  payLineErrorOf,
  paySummaryOf,
  receiptPaymentsOf,
  round2,
  type PayKind,
  type PayLine,
} from "./billingPayModelV4";
import {
  DEPOSIT_KINDS,
  depositHintOf,
  depositReceiptLinesOf,
  type DepositReceiptLine,
  type WalletType,
} from "./billingWalletModelV4";

// Types
export type DepositView = {
  name: string;
  code: string;
  amount: number;
  rows: DepositReceiptLine[];
  change: number;
  note: string;
};

type ModalBillingDepositV4Props = {
  // data
  deposit: DepositState;
  types: WalletType[];
  patient?: { hn?: string | null; full_name?: string } | null;
  error?: unknown;
  busy?: boolean;
  // callback
  onSubmit: (data: DepositData, view: DepositView) => void;
  onCloseError: () => void;
  onClose: () => void;
};

/** pop-up ฝากเงินมัดจำ: รายการเงินมัดจำ จำนวน และรูปแบบการชำระ */
const ModalBillingDepositV4 = (props: ModalBillingDepositV4Props) => {
  const [typeId, setTypeId] = useState("");
  const [amountText, setAmountText] = useState("");
  const [note, setNote] = useState("");
  const [cash, setCash] = useState("");
  const [lines, setLines] = useState<PayLine[]>([]);
  const [tried, setTried] = useState(false);

  const amount = Math.max(0, Number(amountText) || 0);
  const type = props.types.find((item) => String(item.id) === typeId);

  // Memo
  const summary = useMemo(
    () =>
      paySummaryOf({
        items: [],
        calc: {
          subtotal_price: String(amount),
          total_invoice_discount: "0",
          total_voucher_invoice_discount: "0",
          round_off: "0",
          net_payable: String(amount),
        },
        walletTotal: 0,
        cash,
        lines,
      }),
    [amount, cash, lines],
  );

  const hint = depositHintOf({ amount, typeId, cash, summary, lines, tried });
  const disabled =
    props.busy ||
    !(amount > 0) ||
    !typeId ||
    (cash.trim() !== "" && Number.isNaN(Number(cash))) ||
    summary.total < amount ||
    summary.others > amount;

  // Handler
  const handleFillCash = () => {
    const left = round2(Math.max(0, amount - summary.others));

    setCash(left ? String(left) : "");
  };

  const handleAddLine = (kind: PayKind) => {
    const left = round2(Math.max(0, amount - summary.total));

    setLines([...lines, emptyPayLine(kind, left)]);
  };

  const handleChangeLine = (index: number, data: Partial<PayLine>) => {
    setLines(lines.map((line, i) => (i === index ? { ...line, ...data } : line)));
  };

  const handleRemoveLine = (index: number) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleClear = () => {
    setCash("");
    setLines([]);
    setTried(false);
  };

  const handleSubmit = () => {
    if (disabled || !type?.id) {
      return;
    }

    if (lines.some((line) => Object.keys(payLineErrorOf(line)).length > 0)) {
      setTried(true);

      return;
    }

    const cashPaid = round2(Math.max(0, Math.min(summary.cash, amount - summary.others)));

    props.onSubmit(
      {
        wallet_type: type.id,
        payments: receiptPaymentsOf(summary, lines),
        user_remark: note,
      },
      {
        name: type.name,
        code: type.code,
        amount,
        rows: depositReceiptLinesOf(cashPaid, lines, props.deposit.accounts, props.deposit.edcs),
        change: summary.change,
        note,
      },
    );
  };

  // Render
  return (
    <ModalV4
      title="ฝากเงินมัดจำ"
      width={980}
      className="hv4-bill-dep-modal"
      headExtra={
        <span className="hv4-bill-dep-sub">
          {props.patient?.full_name || ""} ({props.patient?.hn || "-"})
        </span>
      }
      onClose={props.onClose}
    >
      <div className="hv4-bill-pay hv4-bill-dep">
        <AlertV4 tone="danger" error={props.error} onClose={props.onCloseError} />

        <div className="hv4-bill-dep-body">
          <div className="hv4-bill-dep-left">
            <div>
              <label className="hv4-label" htmlFor="hv4-dep-type">
                รายการเงินมัดจำ <b className="hv4-bill-pay-req">*</b>
              </label>
              <select
                id="hv4-dep-type"
                className="hv4-input"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
              >
                <option value="">— เลือกรายการเงินมัดจำ —</option>
                {props.types
                  .filter((item) => item.active !== false)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      [{item.code}] {item.name}
                    </option>
                  ))}
              </select>
              <div className="hv4-bill-dep-help">
                ชื่อรายการนี้จะใช้เป็นชื่อ Wallet ที่แสดงในภาพรวม และตอนเลือกตัดชำระ
              </div>
            </div>

            <FieldV4
              label={
                <>
                  จำนวนเงินมัดจำ (บาท) <b className="hv4-bill-pay-req">*</b>
                </>
              }
              htmlFor="hv4-dep-amount"
            >
              <input
                id="hv4-dep-amount"
                className="hv4-input hv4-bill-num hv4-bill-dep-amount"
                inputMode="decimal"
                placeholder="0.00"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
              />
            </FieldV4>

            <FieldV4 label="หมายเหตุ" htmlFor="hv4-dep-note">
              <input
                id="hv4-dep-note"
                className="hv4-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </FieldV4>

            <div className="hv4-bill-dep-remain">
              <label>ยอดคงเหลือต้องชำระ</label>
              <div
                className={`hv4-bill-dep-remain-box${
                  summary.remain > 0 ? " hv4-bill-dep-remain-box--left" : ""
                }`}
              >
                {formatComma(summary.remain)}
              </div>
            </div>
          </div>

          <div className="hv4-bill-dep-right">
            <div className="hv4-bill-pay-title">
              รูปแบบการชำระ{" "}
              <span>
                · เงินสดกรอกได้เลย · ช่องทางอื่นกด + เพื่อเพิ่มรายละเอียดด้านล่าง
                ยอดจะรวมขึ้นมาที่นี่
              </span>
            </div>
            <div className="hv4-bill-pay-methods">
              <div className="hv4-bill-pay-mgrid hv4-bill-dep-mgrid">
                <label htmlFor="hv4-dep-cash">เงินสด</label>
                <input
                  id="hv4-dep-cash"
                  className="hv4-input hv4-bill-num"
                  placeholder="กรอกจำนวนเงิน"
                  inputMode="decimal"
                  value={cash}
                  onChange={(e) => setCash(e.target.value)}
                />
                <button
                  type="button"
                  className="hv4-bill-pay-plus"
                  aria-label="ใส่ยอดที่เหลือเป็นเงินสด"
                  title="ใส่ยอดที่เหลือเป็นเงินสด"
                  onClick={handleFillCash}
                >
                  +
                </button>
                {DEPOSIT_KINDS.map(({ kind, label }) => {
                  const count = lines.filter((line) => line.kind === kind).length;

                  return (
                    <div key={kind} className="hv4-bill-pay-mrow">
                      <label>
                        {label}
                        {count > 0 && <span className="hv4-bill-pay-count"> ({count} รายการ)</span>}
                      </label>
                      <div aria-label={label} className="hv4-bill-pay-sum">
                        {formatComma(summary.kindTotals[kind])}
                      </div>
                      <button
                        type="button"
                        className="hv4-bill-pay-plus"
                        aria-label={`เพิ่มรายละเอียด${label}`}
                        title={`เพิ่มรายละเอียด${label}`}
                        onClick={() => handleAddLine(kind)}
                      >
                        +
                      </button>
                    </div>
                  );
                })}
                <label className="hv4-bill-pay-strong">รวมยอดชำระทั้งหมด</label>
                <div className="hv4-bill-pay-money hv4-bill-pay-money--due">
                  {formatComma(summary.total)}
                </div>
                <span />
                <label>เงินทอน</label>
                <div className="hv4-bill-pay-money">{formatComma(summary.change)}</div>
                <span />
              </div>
            </div>
            <ButtonV4
              variant="ghost"
              size="sm"
              className="hv4-bill-dep-clear"
              onClick={handleClear}
            >
              ล้างการชำระทั้งหมด
            </ButtonV4>
          </div>
        </div>

        {lines.length > 0 && (
          <section aria-label="รายละเอียดการชำระ" className="hv4-bill-pay-detail">
            <div className="hv4-bill-pay-detail-head">
              <b>รายละเอียดการชำระ</b>
            </div>
            {lines.map((line, index) => (
              <BillingPayLineV4
                key={index}
                line={line}
                tried={tried}
                accounts={props.deposit.accounts}
                edcs={props.deposit.edcs}
                onChange={(data) => handleChangeLine(index, data)}
                onRemove={() => handleRemoveLine(index)}
              />
            ))}
          </section>
        )}

        {hint && (
          <div role="alert" className="hv4-bill-dep-hint">
            {hint}
          </div>
        )}

        <div className="hv4-bill-dep-foot">
          <ButtonV4 variant="ghost" onClick={props.onClose}>
            ยกเลิก
          </ButtonV4>
          <ButtonV4 className="hv4-bill-pay-submit" disabled={disabled} onClick={handleSubmit}>
            รับเงิน และออกใบรับมัดจำ
          </ButtonV4>
        </div>
      </div>
    </ModalV4>
  );
};

ModalBillingDepositV4.displayName = "ModalBillingDepositV4";

export default ModalBillingDepositV4;
